/**
 * The install funnel's vocabulary: every count this system is allowed to make, written once
 * and shared by the browser that reports and the API that records (F-123).
 *
 * **Why this is in `@nabu/shared` and not in the browser package.** The words travel over
 * HTTP from a page to a Lambda and then into the *name of a billed CloudWatch metric*. Two
 * copies of this list would be two copies that drift, and the drift would be invisible: the
 * browser would post `notifications` where the filter looks for `granted`, the API would log
 * a line nothing matches, and the dashboard would show a flat zero that reads exactly like
 * "nobody installed it". This project has had that failure — a component reporting success
 * while doing nothing — more than once, and this is a funnel whose whole job is to be
 * believed months from now.
 *
 * **The list is closed, and that is a cost control rather than tidiness.** CloudWatch bills
 * per metric per month, and a metric filter with a dimension creates a *new billed metric for
 * every distinct dimension value it ever sees*. The route arrives in the body of an
 * unauthenticated POST, so without this list a stranger with a loop could mint ten thousand
 * custom metrics at $0.30 each against an account that also runs a live business. The API
 * validates against `isFunnelReport` **before** the value reaches a log line, which is the
 * only place the check can go: once it is in the log, the filter will publish it.
 *
 * The nine pairs below are therefore also the bill. See docs/costs.md.
 */
/**
 * Every pair that may ever be counted — and therefore every metric CloudWatch may ever bill
 * us for from this feature. Nine, at $0.30 a month, plus one for refusals: see docs/costs.md.
 *
 * Deliberately enumerated rather than computed as stages × routes. The cross product is
 * twenty, half of them meaningless — there is no such thing as `offered:ios`, because at the
 * moment of the offer we always know which of the two iOS instructions we showed — and a
 * meaningless pair is not merely untidy, it is thirty cents a month for a line on a
 * dashboard that can never move.
 */
export const FUNNEL_REPORTS = [
    // The offer, split by what we actually put on the screen.
    'offered:prompt',
    'offered:ios-safari',
    'offered:ios-other',
    // The completion, split only by platform, for the reason on `FunnelRoute`.
    'installed:ios',
    'installed:other',
    // Permission, which on iOS can only be asked for from inside an installed app.
    'granted:ios',
    'granted:other',
    // A subscription row. The only one of the four the server observes for itself.
    'reachable:ios',
    'reachable:other',
];
const KNOWN = new Set(FUNNEL_REPORTS);
/**
 * Whether this is something we are allowed to count.
 *
 * Takes `unknown` on purpose: its callers are a JSON body from the open internet and a value
 * read back out of `localStorage`, and a signature that took `FunnelStage` would have to be
 * fed by a cast at exactly the two places where the value is least trustworthy.
 */
export function isFunnelReport(stage, route) {
    return typeof stage === 'string' && typeof route === 'string' && KNOWN.has(`${stage}:${route}`);
}
/** The after-the-fact route: all we can honestly say once an install has happened. */
export function platformRoute(apple) {
    return apple ? 'ios' : 'other';
}
/**
 * Which platform a push subscription belongs to, read from the endpoint the browser handed us.
 *
 * **This is the one number in the funnel the server establishes on its own**, and that is why
 * it is worth deriving rather than accepting from the client. Everything else here is the
 * browser's word for what happened on the browser; a push endpoint is a fact the API holds in
 * its hand. Apple's Web Push service is the only one that answers for an installed iOS app —
 * an iOS tab has no `PushManager` at all — so an endpoint on `web.push.apple.com` *is* a
 * home-screen install, established without asking anybody.
 *
 * Matched on the hostname of a parsed URL rather than on a substring of the string, because
 * `https://evil.example/?x=web.push.apple.com` contains the name and is not Apple. The
 * endpoint has already been through `checkSubscription`, so the parse cannot fail here; it is
 * still wrapped, because this function is one refactor away from being called on something
 * that has not.
 */
export function pushEndpointRoute(endpoint) {
    let host;
    try {
        host = new URL(endpoint).hostname.toLowerCase();
    }
    catch {
        return 'other';
    }
    return host === 'web.push.apple.com' || host.endsWith('.push.apple.com') ? 'ios' : 'other';
}
//# sourceMappingURL=install.js.map